import { memo, useCallback } from "react";
import { useLocation } from "react-router";
import { styled } from "styled-components";

import { Tile } from "common/components";
import { lineTruncationStyle } from "common/styles";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import type { TopicBackLabelData } from "context/session/sessionTypes";
import { useSession } from "context/session/useSession";
import type { TrackPage } from "domains/analytics/types";
import { getUserId } from "domains/authentication/selectors";
import { TopicTileFollowButton } from "domains/topic/components/topicTile/TopicTileFollowButton";
import { TopicTileIcons } from "domains/topic/components/topicTile/TopicTileIcons";
import type { TopicAttachment, TopicProvider } from "entities/topics/types";

const StyledLabelContainer = styled.div`
  padding: 7px 10px;
  border-radius: 5px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 0.9rem;
  height: 30px;
  box-sizing: border-box;
`;

const StyledHiddenContent = styled.span<{ hasSubtitle: boolean }>`
  display: flex;
  flex-direction: column;
  height: 0;
  font-size: 1rem;
  font-weight: 600;
  opacity: 1;
  transition:
    height 0.3s ease,
    padding 0.3s ease;
  overflow: hidden;

  @media (hover: none) {
    height: 1.2rem;
    padding-top: 10px;
  }
`;

const StyledTile = styled(Tile)`
  display: block;

  &:hover ${StyledHiddenContent} {
    height: 1.2rem;
    padding-top: 10px;
  }
`;

const StyledOwnerLabel = styled.p`
  margin: 0;
  ${lineTruncationStyle(1)}
`;

type TopicTileProps = {
  topicId: string;
  topicName: string;
  provider?: TopicProvider;
  attachment?: TopicAttachment;
  imageUrl?: string;
  isCustom?: boolean;
  isFollowing: boolean;
  isNew: boolean;
  canEdit?: boolean;
  className?: string;
  hideFollowAction?: boolean;
  hideOwnershipLabel?: boolean;
  link?: string;
  noLink?: boolean;
  onClick?: () => void;
  topicBack: TopicBackLabelData;
  pageForTracking?: TrackPage;
};

export const TopicTile = memo(
  ({
    topicId,
    topicName,
    attachment,
    imageUrl,
    isCustom = false,
    isFollowing,
    isNew,
    canEdit = false,
    className,
    hideFollowAction = false,
    hideOwnershipLabel = false,
    link,
    noLink,
    onClick,
    provider,
    topicBack,
    pageForTracking,
  }: TopicTileProps) => {
    const { hash, pathname, search } = useLocation();
    const userId = useSelector(getUserId);
    const { updateSession } = useSession();
    const translate = useTranslate();

    const TopicTileIconsWithOwnerLabel = useCallback(() => {
      const ownerLabel = "app.create.share_settings.owner";
      const sharedLabel = canEdit ? "app.create.share_settings.editor" : "app.create.share_settings.viewer";
      const label = provider?.id === userId ? ownerLabel : sharedLabel;

      return (
        <>
          {isCustom && !hideOwnershipLabel && <StyledLabelContainer>{translate(label)}</StyledLabelContainer>}
          <TopicTileIcons attachment={attachment} isNew={isNew} />
        </>
      );
    }, [attachment, canEdit, hideOwnershipLabel, isCustom, isNew, provider?.id, translate, userId]);

    const TopicProviderLabel = useCallback(() => {
      if (isCustom && !hideOwnershipLabel && provider?.id !== userId) {
        return (
          <StyledHiddenContent hasSubtitle={provider?.id !== userId}>
            <StyledOwnerLabel>
              {translate("app.create.share_settings.owner")}: {provider?.name}
            </StyledOwnerLabel>
          </StyledHiddenContent>
        );
      }

      return undefined;
    }, [hideOwnershipLabel, isCustom, provider?.id, provider?.name, translate, userId]);

    const onClickTile = () => {
      updateSession((state) => ({ ...state, topicBack: { ...topicBack, path: `${pathname}${search}${hash}` } }));
      onClick?.();
    };

    return (
      <StyledTile
        className={className}
        image={imageUrl}
        leftElement={<TopicTileIconsWithOwnerLabel />}
        link={noLink ? undefined : (link ?? `/topics/${topicId}`)}
        onClick={onClickTile}
        rightElement={
          !hideFollowAction ? (
            <TopicTileFollowButton
              id={topicId}
              name={topicName}
              isFollowing={isFollowing}
              pageForTracking={pageForTracking}
            />
          ) : undefined
        }
        subElement={<TopicProviderLabel />}
        title={topicName}
      />
    );
  },
);

TopicTile.displayName = "TopicTile";
